import { GeoJSONSourceSpecification } from 'maplibre-gl';

/** For MapBox layers that have a GeoJSON source. */
export const emptyGeoJSONSource: GeoJSONSourceSpecification = {
  data: {
    features: [],
    type: 'FeatureCollection',
  },
  type: 'geojson',
};

export const DEFAULT_REPORT_PANEL_WIDTH = 320;

export const PORTFOLIO_LABEL_MAX_LENGTH = 90;

export const ONE_MB = 1_048_576;

export const THREE_MB = ONE_MB * 3;

export const SIX_MB = ONE_MB * 6;

export const ONE_HUNDRED_MB = ONE_MB * 100;
export const ONE_HUNDRED_FIFTY_MB = ONE_MB * 150;

/** This is the zoom level at with the comp chips will appear on the map */
export const MIN_COMP_CHIPS_ZOOM_LEVEL: number = 12;

/**
 * @Note the min padding that content should have in the plat map
 */
export const PLAT_MAP_CONTENT_MIN_PADDING = 16;

export const TOP_NAV_HEIGHT_PX = 48;

// Date formats
export const ACRES_DATE_FORMAT = 'MM-DD-YYYY';
export const ACRES_DATE_FORMAT_DATE_PICKER = 'MM-dd-yyyy';

export const IS_PORTFOLIO_MAP_HIDDEN = 'isPortfolioMapHidden';

/** Local storage key for acres announcements */
export const ACRES_ANNOUNCMENTS = 'acres-announcements';

export const PASSWORD_REQUIREMENTS = [
  'Have between 8 and 72 characters',
  'Not contain repetitive or sequential characters',
  'Not be the same as the account name',
  'Not be a common password',
];

export const TABS = {
  BASE_MAPS: 'Base Maps',
  BOUNDARIES: 'Boundary Tools',
  CUSTOMIZE: 'Customize',
  INSIGHTS: 'Insights',
  LAYERS: 'Layers',
  LEAD_LIST: 'Lead List',
  LISTINGS: 'Listings',
  MORTGAGE: 'Mortgage',
  PORTFOLIO: 'Portfolio',
  SAVED_LAND_PLAT_MAP: 'Saved Maps',
  SEARCH: 'Search',
  SOLD_LAND: 'Sold Land',
};

export enum AcresFeatureFlags {
  ENTERPRISE_TOS = 'config-enterprise-tos',
  INSIGHTS_CONFIG = 'config-insights',
  ORY_SESSION_CONFIG = 'temp-config-ory-session',
  TILE_CONFIG = 'config-tiles',
  CONFIG_COMP_LISTINGS_ACCORDION_DEFAULTS = 'config-comp-listings-accordion-defaults',
  INSIGHTS_REPORT_LIMITS = 'insights-report-limits',
  CLONE_BOUNDARY = 'clone-boundary',
  CONFIG_LEAD_LIST = 'config-mailing-list',
  SSO_OIDC_CLIENTS = 'sso-oidc-clients',
  TEMP_LLAMA_DEED_PLOTTER = 'temp-llama-deed-plotter',
  TEMP_KEPLER_ENABLED = 'temp-kepler-enabled',
  TEMP_KEPLER_FILTERS_ENABLED = 'temp-kepler-filters-enabled',
  TEMP_ORG_MANAGEMENT = 'temp-org-management',
  TEMP_COMP_BUYER_SELLER_FILTERS = 'temp-comp-buyer-seller-filters',
  TEMP_TOOL_TOP_BAR = 'temp-tool-top-bar',
  TEMP_NEW_SELECTIONS_PANEL = 'temp-new-selections-panel',
  TEMP_NEW_LAYERS = 'temp-new-layers',
  TEMP_DEED_AI_VERSION_2 = 'temp-deed-ai-version-2',
}

/** The type of remote config "CONFIG_INSIGHTS_REPORTS_LIMITS" */
export type InsightsReportLimitsConfig = {
  report: {
    acreage: number;
    boundaries: number;
  };
  organization: {
    free: number;
    plus: number;
  };
  'large-report': {
    acreage: number;
    boundaries: number;
  };
};

export const LOCAL_STORAGE_LAST_ACTIVE = 'last-active';

// set when Sign Up Completed event is tracked by Segment
export const LOCAL_STORAGE_SIGN_UP = 'sign-up';
export const LOCAL_STORAGE_SIGN_UP_TRACKED = 'sign_up_tracked';
export const LOCAL_STORAGE_SOCIAL_SIGN_UP_DATA = 'social_sign_up_data';
export const REDIRECT_FROM = 'redirect_from';

export const LATEST_LOGIN_ATTEMPT_DATA = 'latest_successful_login_data';
export type LatestLoginAttemptData = {
  email: string;
  method: string;
  provider: string;
  csrf_token: string;
};

export const MAX_META_DESCRIPTION_LENGTH = 156;

/** IDs of HTML elements */
export enum HTML_ELEMENTS {
  BOTTOM_NAV_CONTAINER = 'bottom-nav-container',
  INSIGHTS_CONTAINER = 'insights-container',
}
export const FEATURE_HUB_HOST = 'https://flags.acres.com';

export const LOCAL_STORAGE_THEME = 'theme';

export enum LoadingStatus {
  NO_DATA,
  UNLOADED,
  LOADING,
  LOADED,
}
export const AUTH_ACTIONS = {
  CREATE_ACCOUNT: 'Create Account',
  LOGIN: 'Log In',
  LOGIN_TO_VIEW: 'Log In To View',
  LOGOUT: 'Log Out',
  SIGN_UP: 'Sign Up',
  UPGRADE: 'Upgrade',
  UPGRADE_TO_ACCESS: 'Upgrade to access',
};

export const PRICING_PAGE_FREE_TRIAL_CTA = 'Start Free Trial';
/**
 * Map view settings for centering on the USA
 */
export const initialMapView = {
  latitude: 40.5,
  longitude: -99,
  zoom: 3,
};

/** This is our default font ordering */
export const fontString = 'Roboto, sans-serif';

/**
 * Used to set the font for anything inside of Mapbox map (markers, county names, etc)
 */
export const mapboxStyleFont = {
  fontFamily: fontString,
};

export const GLOBAL_TOOLTIP = 'global-tooltip';
export const INSIGHT_PANEL_SCROLLING_DIV_ID = 'insights-panel-scrolling-div';

export const antiScreenshotElementProps = {
  'data-html2canvas-ignore': true, // This prevents the element from being rendered during a screenshot
};

export const CONUS_BOUNDS = [
  // Contiguous United States
  [-147.69155, 20.45517],
  [-52.897148, 51.2826732],
];
